/**
 * @file useFooterColumns.ts
 * @description Колонки подвала для вкладки «Подвал» (phase19.16, приёмка
 * Р2а; SB113–SB115; uiux-footer-columns §4.4, §5): меню подвала из
 * черновика + категории автосписка + документ «Условия возврата» → общая
 * раскладка `footerLayout` (как у сервера и витрины). Считается по данным,
 * ничего не хранит сам (один источник правды — меню черновика).
 */

import { footerHiddenPlatformLinks, type FooterLayout, type FooterPlatformLink, type SiteTheme } from "@chaiberi/shared";
import { useMemo } from "react";

import { useFooterSources } from "./useFooterSources";
import { useSiteMenus } from "./useSiteMenus";
import type { LegalTarget } from "../../lib/api-legal-targets";
import { cabinetFooterLayout } from "../../lib/site-view/footer-columns";
import type { FooterLegalDocsState } from "../../lib/site-view/footer-link-where";
import type { SiteBrandDraft, SiteMenuItem } from "../../lib/site-view/types";

export interface FooterColumnsState {
  /** null — меню ещё не загружено (или ошибка). */
  layout: FooterLayout<SiteMenuItem> | null;
  error: string | null;
  reload: () => void;
  /** Известно ли число категорий автосписка. */
  categoriesKnown: boolean;
  /** Ссылки платформы, которые прячутся из-за пунктов меню (SB115). */
  hiddenLinks: ReadonlySet<FooterPlatformLink>;
  /** Состояние документов оферты, политики, возврата (SB121 «где задаётся»). */
  legalDocs: FooterLegalDocsState;
  /** Документы и группы магазина — свои строки «Ссылок» (SB142); null — не загружены. */
  legalTargets: LegalTarget[] | null;
  reloadLegal: () => void;
}

export function useFooterColumns(footer: SiteTheme["footer"], brand: SiteBrandDraft): FooterColumnsState {
  const menus = useSiteMenus();
  const sources = useFooterSources();
  const footerMenu = menus.menus?.FOOTER ?? null;

  const layout = useMemo(
    () =>
      footerMenu
        ? cabinetFooterLayout(footerMenu, {
            footer,
            brand,
            returnsDocId: sources.returnsDocId,
            catalogCategoriesCount: sources.autoCategories?.length,
          })
        : null,
    [footerMenu, footer, brand, sources],
  );
  const hiddenLinks = useMemo(() => (layout ? footerHiddenPlatformLinks(layout) : new Set<FooterPlatformLink>()), [layout]);

  return {
    layout,
    error: menus.error,
    reload: () => void menus.reload(),
    categoriesKnown: sources.autoCategories !== null,
    hiddenLinks,
    legalDocs: sources.legalDocs,
    legalTargets: sources.legalTargets,
    reloadLegal: sources.reloadLegal,
  };
}
